// キャンバス全体像の PDF スナップショットをクライアントで生成する（Connect Note 連携用）。
// 既存の高解像度 PNG 描画（renderCanvasExportImage）を流用し、pdf-lib で 1 ページ PDF に埋め込む。
// Lambda での PDF 描画（日本語フォント問題）を避けるため、ブラウザの Canvas2D 描画を正本とする。

import type { CanvasFrame, CanvasObject } from "@refinement-canvas/domain";
import { PDFDocument } from "pdf-lib";
import {
  EXPORT_WIDTH_PX,
  MIN_EXPORT_WIDTH_PX,
  renderCanvasExportImage,
} from "./canvas-pdf-export";

// 自動縮小の縮小係数（毎回この割合まで幅を下げる）と最大試行回数。EXPORT_WIDTH_PX(3508) から
// MIN_EXPORT_WIDTH_PX(1000) まで 0.75 ずつ下げると 5 段で到達するため、6 回あれば下限まで届く。
const DOWNSCALE_FACTOR = 0.75;
const MAX_DOWNSCALE_ATTEMPTS = 6;
// 自動保存スナップショット（Connect Note 連携の補助プレビュー）の既定描画幅。手動 PDF エクスポート
// （EXPORT_WIDTH_PX=3508、A4 横 300DPI 相当）より低くして、約 20 秒ごとに走るメインスレッドのラスタライズ
// （renderCanvasExportImage の toDataURL）コストと downscaleToFit の反復回数を抑える。全体像は縮小表示の
// 補助プレビューで、詳細はキャンバスを開いて確認する用途のため、この解像度で十分。手動エクスポートは全解像度のまま。
const SNAPSHOT_EXPORT_WIDTH_PX = 2000;

/** data URL（image/png）から PNG バイト列を取り出す。pdf-lib は Uint8Array を要求する。 */
function pngBytesFromDataUrl(dataUrl: string): Uint8Array | null {
  // data:image/png;base64 の base64 本体を 1 回だけデコードする（dataUrlToPngBlob と同じ検証）。
  // 注: ネイティブデコード（fetch(dataUrl).arrayBuffer()）は、配信時 CSP の connect-src が data: を許可して
  // いない（'self' idp https: wss:）ため enforce 昇格時にブロックされ得る。よって atob ベースのまま残し、
  // メインスレッド負荷は自動保存スナップショットの描画幅低減（SNAPSHOT_EXPORT_WIDTH_PX）で抑える。
  const [header, body] = dataUrl.split(",");
  if (!header?.startsWith("data:image/png;base64") || !body) {
    return null;
  }
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** 指定描画幅で全体像 1 ページ PDF の Blob を生成する。描画失敗時は null。 */
async function renderPdfBlobAtWidth(
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  exportWidthPx: number,
  // 未確定のローカル専用画像（blob: URL の本人プレビュー）を全体像へ描かないか。
  // 既定 true は自動保存スナップショット（Connect Note へ外部配信）向け。手動ダウンロードでは false。
  excludeUnpublishedSrc = true
): Promise<Blob | null> {
  // 全体像スナップショットは Connect Note へ外部配信されるため、未確定のローカル専用画像（blob: URL の
  // 本人用プレビュー。サーバ未公開で他参加者にも配信されない）は描かない（未公開の本人ローカル画像を外部へ
  // 載せない）。公開 URL へ finalize 済みの画像は通常どおり描画する。
  const pngDataUrl = await renderCanvasExportImage(objects, canvasRect, exportWidthPx, {
    excludeUnpublishedSrc,
  });
  if (!pngDataUrl) {
    return null;
  }
  const pngBytes = pngBytesFromDataUrl(pngDataUrl);
  if (!pngBytes) {
    return null;
  }
  const pdf = await PDFDocument.create();
  const png = await pdf.embedPng(pngBytes);
  // PNG の実寸でページを作り、余白 0 で全面に配置する（全体像をそのまま 1 ページに収める）。
  const page = pdf.addPage([png.width, png.height]);
  page.drawImage(png, { x: 0, y: 0, width: png.width, height: png.height });
  const bytes = await pdf.save();
  return new Blob([bytes], { type: "application/pdf" });
}

/**
 * `generate(width)` を EXPORT_WIDTH_PX から段階的に縮小しながら呼び、生成 Blob が maxBytes 以内に
 * 収まった時点で返す。下限幅（MIN_EXPORT_WIDTH_PX）まで縮めても収まらない場合は、その下限幅の Blob を
 * ベストエフォートで返す（呼び出し側が最終的な上限超過を判断する）。maxBytes 未指定なら初回（最大解像度）を返す。
 * 描画失敗（null）はそのまま null を返す。レンダリングへの依存を持たない純粋なループとしてテスト可能にする。
 */
export async function downscaleToFit(
  generate: (exportWidthPx: number) => Promise<Blob | null>,
  maxBytes: number | undefined,
  startWidthPx: number = EXPORT_WIDTH_PX
): Promise<Blob | null> {
  let width = startWidthPx;
  for (let attempt = 0; attempt < MAX_DOWNSCALE_ATTEMPTS; attempt += 1) {
    const blob = await generate(width);
    if (!blob) {
      return null;
    }
    if (maxBytes === undefined || blob.size <= maxBytes || width <= MIN_EXPORT_WIDTH_PX) {
      return blob;
    }
    width = Math.max(MIN_EXPORT_WIDTH_PX, Math.round(width * DOWNSCALE_FACTOR));
  }
  // 現状の定数（DOWNSCALE_FACTOR=0.75 / MIN_EXPORT_WIDTH_PX=1000 / MAX_DOWNSCALE_ATTEMPTS=6）では、
  // 手動エクスポートの開始幅 3508 は 3508→2631→1973→1480→1110→1000 と推移し最後の試行（attempt=5）で
  // width=MIN に達し、自動保存スナップショットの開始幅 2000 は 2000→1500→1125→1000 とより早く MIN に達する。
  // いずれもループ内の `width <= MIN_EXPORT_WIDTH_PX` で必ず return するため、この末尾 return には到達しない。
  // 定数や開始幅を変更し、下限幅へ到達する前に試行回数を使い切る構成にした場合の安全網として、下限幅で
  // ベストエフォート生成して返す（現状到達不能だが、関数の戻り値保証と将来のガードのために残す）。
  return generate(MIN_EXPORT_WIDTH_PX);
}

/**
 * キャンバスオブジェクト群から、全体像 1 ページ PDF（application/pdf）の Blob を生成する。
 * maxBytes を指定すると、大規模キャンバスで PDF が上限を超える場合は描画幅を段階的に下げて上限内へ
 * 収める（古い世代で凍結させず、低解像度でも最新の全体像を保存できるようにする）。
 * 描画に失敗した場合（PNG 生成不可など）は null を返す。
 */
export async function renderCanvasExportPdfBlob(
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  maxBytes?: number
): Promise<Blob | null> {
  // 自動保存スナップショットは手動エクスポートより低い開始幅で描画し、メインスレッド占有を抑える。
  // 外部配信のため未確定のローカル専用画像は描かない（excludeUnpublishedSrc=true 既定）。
  return downscaleToFit(
    (width) => renderPdfBlobAtWidth(objects, canvasRect, width),
    maxBytes,
    SNAPSHOT_EXPORT_WIDTH_PX
  );
}

/**
 * 手動 PDF ダウンロード用に、表示中の全オブジェクトから全体像 1 ページ PDF（application/pdf）の Blob を生成する。
 * 自動保存スナップショットとの違い:
 * - 開始幅を最高解像度（EXPORT_WIDTH_PX=3508、A4 横 300DPI 相当）にする（ローカル保存なので画質優先）。
 * - 未確定のローカル専用画像（本人の blob プレビュー）も描く（印刷ボタンと同じく「表示中の最新状態」を出力する）。
 * maxBytes を指定した場合のみ、上限超過時に描画幅を段階的に下げる（未指定なら最高解像度のまま。ローカル保存では上限不要）。
 * 描画失敗時は null。
 */
export async function renderCanvasDownloadPdfBlob(
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  maxBytes?: number
): Promise<Blob | null> {
  return downscaleToFit(
    (width) => renderPdfBlobAtWidth(objects, canvasRect, width, false),
    maxBytes,
    EXPORT_WIDTH_PX
  );
}
